import React, { useCallback, useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import API_BASE from "./apiBase";
import { displayName, useMaskNames, MaskToggleButton } from "./nameMask";
import { fmtEta, etaBasisText } from "./etaFormat";

/**
 * ===========================================================================
 * CUSTOMERS
 * ===========================================================================
 * One register per group. A customer is not attached to a branch — their
 * DELIVERIES are. So Mrs Jones is one record, and a parcel from Frome and a
 * parcel from Bath are two rows in her item list, each showing its branch.
 *
 * THE ITEM LIST IS ONE LIST WITH FILTERS, not three panels. "Deliveries",
 * "items in a location at the branch" and "items awaiting collection" all live
 * in the same table and they overlap — a collection parcel sitting in a tote
 * satisfies two at once. Three panels would show that parcel twice and invite
 * somebody to add the totals up.
 *
 * SEARCH IS SERVER-SIDE, unlike the head-office grids. Those filter in the
 * browser because "today" bounds them to hundreds of rows. This register only
 * grows, so it is paged and searched in SQL, debounced from here.
 */

const S = {
  wrap:   { padding: 16, fontFamily: "system-ui, sans-serif", color: "#111827" },
  h1:     { fontSize: 18, fontWeight: 700, margin: 0 },
  panel:  { border: "1px solid #d1d5db", borderRadius: 10, background: "#fcfcfd",
            padding: "12px 14px" },
  label:  { fontSize: 10.5, fontWeight: 800, color: "#6b7280",
            textTransform: "uppercase", letterSpacing: 0.5 },
  input:  { padding: "6px 8px", fontSize: 13, border: "1px solid #d1d5db",
            borderRadius: 4, width: "100%", boxSizing: "border-box" },
  table:  { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th:     { textAlign: "left", padding: "6px 8px", borderBottom: "2px solid #e5e7eb",
            fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4,
            color: "#6b7280", whiteSpace: "nowrap" },
  td:     { padding: "6px 8px", borderBottom: "1px solid #f3f4f6",
            verticalAlign: "top" },
  muted:  { color: "#6b7280", fontSize: 11 },
  btn:    { background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db",
            borderRadius: 4, padding: "6px 10px", fontSize: 13, cursor: "pointer" },
  btnGo:  { background: "#0f766e", color: "#fff", border: "1px solid #0f766e",
            borderRadius: 4, padding: "6px 10px", fontSize: 13, cursor: "pointer" },
  error:  { background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b",
            padding: 10, borderRadius: 6, fontSize: 13 },
};

/**
 * This API speaks snake_case and the shared formatter speaks camelCase, so the
 * translation happens once, here, rather than at every call site.
 */
const etaLine = (it) => fmtEta({
  etaMinutes: it.eta_minutes,
  etaAt: it.eta_at,
  etaMiles: it.eta_miles,
});

const STATUS_BG = {
  delivered: "#dcfce7", failed: "#fee2e2", on_round: "#fef3c7",
  // In the van: picked up but not back at the depot yet. Amber like on_round,
  // because it is still out with a driver, just a shade warmer.
  collected: "#ffedd5",
  pending: "#f3f4f6", returned: "#e0e7ff", cancelled: "#f3f4f6",
};
const STATUS_LABEL = {
  pending: "Waiting", on_round: "Out with a driver", delivered: "Delivered",
  failed: "Failed", returned: "Returned", cancelled: "Cancelled",
};

/**
 * A COLLECTION RUNS THE OTHER WAY, so the same status word means a different
 * thing on it, and the label says what is true of the parcel rather than
 * what the column is called:
 *
 *   pending     allocated to a driver, who will see it from the day it is due
 *   on_round    the driver is out and it is on his route
 *   collected   he has been to the door and it is in his van (its own status,
 *               migration 015, because "on round" and "done" were both wrong)
 *   delivered   he has put it on the branch's Collections shelf - delivered
 *               to the depot, which is what a collection is for
 */
const COLLECTION_LABEL = {
  pending: "To collect", on_round: "Out to collect", collected: "In driver's van",
  delivered: "At depot (Collections)", failed: "Collection failed",
  returned: "Returned", cancelled: "Cancelled",
};
const statusLabel = (it) => (it.order_type === "collection"
  ? (COLLECTION_LABEL[it.status] || it.status)
  : (STATUS_LABEL[it.status] || it.status));

// Order matters: this is the order the chips appear in, reading left to right
// from "everything" through "still to do" to "finished".
const FILTERS = [
  { key: "all",         label: "All",              count: "all_items" },
  { key: "outstanding", label: "Outstanding",      count: "outstanding" },
  { key: "on_shelf",    label: "On a shelf/tote",  count: "on_shelf" },
  { key: "collection",  label: "Awaiting collection", count: "collection" },
  { key: "out",         label: "Out with a driver", count: "out" },
  { key: "delivered",   label: "Delivered",        count: "delivered" },
  { key: "failed",      label: "Failed",           count: "failed" },
  { key: "closed",      label: "Returned/cancelled", count: "closed" },
];

async function api(path, token, options = {}) {
  const res = await fetch(`${API_BASE}/api/customers${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    throw new Error((data && data.error && data.error.message) || `Request failed (${res.status})`);
  }
  return data;
}

const fmtDate = (d) => {
  if (!d) return "—";
  const t = new Date(d);
  return Number.isNaN(t.getTime()) ? "—" : t.toLocaleDateString("en-GB");
};

/**
 * Date AND time, for a booked collection.
 *
 * The pool runs with dateStrings, so a DATETIME arrives as
 * '2026-08-27 10:00:00.000' and is UTC. Date.parse reads that shape as LOCAL
 * time, which would put every appointment an hour out through British summer,
 * so the zone is made explicit before parsing.
 */
/**
 * What has happened to a parcel, in order, from the timestamps that record
 * it. Only lines whose event has occurred; nothing is inferred from status
 * alone, so a row whose data and status disagree shows the data.
 */
const eventLines = (it) => {
  const lines = [];
  const isColl = it.order_type === "collection";
  if (isColl) {
    if (it.collected_at) lines.push(`Collected ${fmtDateTime(it.collected_at)}`);
    if (it.returned_at) lines.push(`Returned to depot ${fmtDateTime(it.returned_at)}`);
    if (it.status === "failed" && it.last_attempt_at) {
      lines.push(`Last attempt ${fmtDateTime(it.last_attempt_at)}`);
    }
  } else {
    if (it.handed_over_at && (it.status === "on_round" || it.status === "failed")) {
      lines.push(`Into the van ${fmtDateTime(it.handed_over_at)}`);
    }
    if (it.status === "delivered" && it.last_attempt_at) {
      lines.push(`Delivered ${fmtDateTime(it.last_attempt_at)}`);
    }
    if (it.status === "failed" && it.last_attempt_at) {
      lines.push(`Last attempt ${fmtDateTime(it.last_attempt_at)}`);
    }
  }
  return lines;
};

const fmtDateTime = (d) => {
  if (!d) return "—";
  const raw = String(d);
  const iso = /Z$|[+-]\d{2}:?\d{2}$/.test(raw) ? raw : `${raw.replace(" ", "T")}Z`;
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return "—";
  return `${t.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })} `
    + `at ${t.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
};

/**
 * Print a parcel label.
 *
 * THE QR CONTAINS THE BARCODE, NOTHING ELSE. The driver's scanner resolves it
 * with GET /driver/deliveries?barcode=X, so that is what has to be in there.
 * Encoding the name and address as well is tempting and wrong: a label printed
 * on Monday would carry Monday's address, and scanning it on Tuesday would
 * quietly reinstate what somebody corrected in between. The details are printed
 * for a human to read; the code is for the machine to look up.
 *
 * Opens a print window rather than printing the page — printing the page would
 * put the whole management screen on the label stock.
 */
async function printLabel(item, customer) {
  const code = item.barcode || `W${item.delivery_id}`;
  const dataUrl = await QRCode.toDataURL(code, { width: 300, margin: 1 });

  const esc = (v) => String(v ?? "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  const w = window.open("", "_blank", "width=460,height=640");
  if (!w) {
    // Pop-up blockers are silent. Saying so beats a button that appears dead.
    throw new Error("Your browser blocked the print window. Allow pop-ups for this site and try again.");
  }

  w.document.write(`<!doctype html><html><head><title>Label ${esc(code)}</title>
    <style>
      @page { margin: 6mm; }
      body { font-family: system-ui, sans-serif; margin: 0; padding: 8px;
             width: 62mm; }
      .code { text-align: center; }
      .code img { width: 42mm; height: 42mm; }
      .ref  { font-family: monospace; font-size: 13px; text-align: center;
              letter-spacing: 1px; margin-top: 2px; }
      .name { font-size: 15px; font-weight: 700; margin-top: 8px; }
      .addr { font-size: 13px; line-height: 1.35; }
      .post { font-size: 15px; font-weight: 700; letter-spacing: 1px; }
      .meta { font-size: 11px; color: #444; margin-top: 8px;
              border-top: 1px solid #999; padding-top: 4px; }
      .flag { font-size: 12px; font-weight: 700; }
    </style></head><body>
    <div class="code"><img src="${dataUrl}" alt="" /></div>
    <div class="ref">${esc(code)}</div>
    <div class="name">${esc(customer.full_name)}</div>
    <div class="addr">${esc(customer.address || "")}</div>
    <div class="post">${esc(customer.postcode || "")}</div>
    ${customer.phone ? `<div class="addr">${esc(customer.phone)}</div>` : ""}
    ${customer.delivery_note ? `<div class="addr"><i>${esc(customer.delivery_note)}</i></div>` : ""}
    <div class="meta">
      ${esc(item.branch_name)}${item.description ? ` &middot; ${esc(item.description)}` : ""}
    </div>
    ${item.order_type === "collection" ? '<div class="flag">COLLECTION</div>' : ""}
    ${item.requires_pin ? '<div class="flag">CODE REQUIRED AT DOOR</div>' : ""}
    ${item.is_urgent ? '<div class="flag">URGENT</div>' : ""}
  </body></html>`);
  w.document.close();

  // The image must be decoded before the print dialogue opens, or the label
  // prints with an empty square where the QR should be.
  w.onload = () => { w.focus(); w.print(); };
}

export default function Customers({ token: tokenProp } = {}) {
  const [token] = useState(() =>
    tokenProp || localStorage.getItem("token") || localStorage.getItem("authToken") || "");

  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [adding, setAdding] = useState(false);

  // Debounced so a search box does not fire a query per keystroke. The ref
  // holds the pending timer so a fast typist cancels their own earlier calls
  // rather than racing them — without it, results can arrive out of order and
  // the list settles on whatever the SLOWEST query returned.
  const timer = useRef(null);

  const load = useCallback(async (term) => {
    setLoading(true);
    setError(null);
    try {
      const q = term ? `?search=${encodeURIComponent(term)}` : "";
      const data = await api(`${q}`, token);
      setRows(data.customers || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => load(search), 250);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [search, load]);

  if (!token) {
    return <div style={S.wrap}><div style={S.error}>Not signed in.</div></div>;
  }

  return (
    <div style={S.wrap}>
      <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 12,
                    flexWrap: "wrap" }}>
        <h1 style={S.h1}>Customers</h1>
        <MaskToggleButton />
      </div>

      {error && <div style={{ ...S.error, marginBottom: 12 }}>{error}</div>}

      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        {/* ---------------- list ---------------- */}
        <div style={{ ...S.panel, flex: "0 0 380px", maxWidth: 380 }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, address, postcode, phone, reference or bag"
              style={S.input}
            />
            <button style={S.btnGo} onClick={() => setAdding(true)}>New</button>
          </div>

          <div style={{ ...S.muted, marginBottom: 6 }}>
            {loading ? "Searching…"
              : `${rows.length} shown of ${total} customer${total === 1 ? "" : "s"}`}
          </div>

          <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
            {rows.length === 0 && !loading ? (
              <div style={S.muted}>
                {search ? `Nothing matches “${search}”.` : "No customers yet."}
              </div>
            ) : (
              rows.map((c) => (
                <CustomerRow key={c.id} c={c}
                  active={c.id === selectedId}
                  onSelect={() => setSelectedId(c.id)} />
              ))
            )}
          </div>
        </div>

        {/* ---------------- detail ---------------- */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {adding ? (
            <NewCustomer token={token}
              onCancel={() => setAdding(false)}
              onCreated={(id) => { setAdding(false); setSelectedId(id); load(search); }} />
          ) : selectedId ? (
            <CustomerDetail key={selectedId} id={selectedId} token={token}
              onChanged={() => load(search)} />
          ) : (
            <div style={{ ...S.panel, color: "#6b7280", fontSize: 13 }}>
              Pick a customer on the left, or add a new one.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CustomerRow({ c, active, onSelect }) {
  const masked = useMaskNames();
  return (
    <div onClick={onSelect}
      style={{
        padding: "8px 8px", borderRadius: 6, cursor: "pointer", marginBottom: 4,
        border: active ? "2px solid #0f766e" : "1px solid #e5e7eb",
        background: active ? "#f0fdfa" : "#fff",
      }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
        <div style={{ fontWeight: 600, fontSize: 13 }}>
          {displayName(c.full_name, masked)}
        </div>
        {c.outstanding > 0 && (
          <span style={{ background: "#fef3c7", color: "#b45309", fontWeight: 600,
                         fontSize: 11, borderRadius: 10, padding: "1px 8px",
                         whiteSpace: "nowrap" }}>
            {c.outstanding} out
          </span>
        )}
      </div>
      <div style={S.muted}>
        {[c.address, c.postcode].filter(Boolean).join(", ") || "No address"}
      </div>
    </div>
  );
}

function CustomerDetail({ id, token, onChanged }) {
  const masked = useMaskNames();
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [addingDelivery, setAddingDelivery] = useState(false);

  const loadCustomer = useCallback(async () => {
    try { setData(await api(`/${id}`, token)); }
    catch (e) { setError(e.message); }
  }, [id, token]);

  const loadItems = useCallback(async (f) => {
    try {
      const d = await api(`/${id}/items?filter=${f}`, token);
      setItems(d.items || []);
    } catch (e) { setError(e.message); }
  }, [id, token]);

  useEffect(() => { loadCustomer(); }, [loadCustomer]);
  useEffect(() => { loadItems(filter); }, [filter, loadItems]);

  if (error) return <div style={S.error}>{error}</div>;
  if (!data) return <div style={{ ...S.panel, ...S.muted }}>Loading…</div>;

  const c = data.customer;
  const counts = data.counts || {};

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={S.panel}>
        <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>
              {displayName(c.full_name, masked)}
              {!c.is_active && (
                <span style={{ ...S.muted, marginLeft: 8 }}>(inactive)</span>
              )}
            </div>
            <div style={{ fontSize: 13, marginTop: 2 }}>
              {[c.address, c.postcode].filter(Boolean).join(", ") || "No address on file"}
            </div>
            <div style={{ fontSize: 13, marginTop: 2 }}>
              {c.phone ? <a href={`tel:${c.phone}`}>{c.phone}</a> : <span style={S.muted}>No phone</span>}
              {c.email && <> · <a href={`mailto:${c.email}`}>{c.email}</a></>}
            </div>
            <NotifyPreference customerId={c.id} hasEmail={!!c.email} />
            {c.external_ref && (
              <div style={S.muted}>Reference {c.external_ref}</div>
            )}
            {c.delivery_note && (
              <div style={{ marginTop: 6, fontSize: 12, background: "#eff6ff",
                            border: "1px solid #bfdbfe", borderRadius: 4,
                            padding: "4px 8px" }}>
                {c.delivery_note}
              </div>
            )}
            {/* original_address is what the customer first gave. Shown only
                when it differs, so a correction can be told from a
                redirection — otherwise it is noise on every record. */}
            {c.original_address && c.original_address !== c.address && (
              <div style={{ ...S.muted, marginTop: 4 }}>
                Originally given as: {c.original_address}
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button style={S.btn} onClick={() => setEditing(true)}>Edit</button>
            <button style={S.btnGo} onClick={() => setAddingDelivery(true)}>
              Add a delivery
            </button>
          </div>
        </div>

        {/* Which branches send this person parcels. The single register makes
            this visible for the first time — under the old per-branch shape
            these were two unrelated records. */}
        {data.branches && data.branches.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <div style={S.label}>Branches</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
              {data.branches.map((b) => (
                <span key={b.account_id}
                  style={{ border: "1px solid #e5e7eb", borderRadius: 12,
                           padding: "2px 10px", fontSize: 12 }}>
                  {displayName(b.name, masked)}
                  <span style={S.muted}>
                    {" "}· {b.parcels} parcel{b.parcels === 1 ? "" : "s"}
                    {b.outstanding > 0 ? `, ${b.outstanding} out` : ""}
                  </span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Intake template placeholder. record_templates already exists,
            group-scoped and versioned, so the form will be a template with
            category 'customer_intake' rather than a new mechanism. */}
        <div style={{ marginTop: 10, ...S.muted, fontStyle: "italic" }}>
          Intake questions are not set up yet — this is where your
          organisation's own questions will appear.
        </div>
      </div>

      {editing && (
        <EditCustomer customer={c} token={token}
          onCancel={() => setEditing(false)}
          onSaved={() => { setEditing(false); loadCustomer(); onChanged && onChanged(); }} />
      )}

      {addingDelivery && (
        <NewDelivery customerId={id} customer={c} token={token}
          onCancel={() => setAddingDelivery(false)}
          onCreated={() => {
            setAddingDelivery(false);
            loadCustomer();
            loadItems(filter);
            onChanged && onChanged();
          }} />
      )}

      <div style={S.panel}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
          {FILTERS.map((f) => {
            const n = counts[f.count] ?? 0;
            const on = filter === f.key;
            return (
              <button key={f.key} onClick={() => setFilter(f.key)}
                style={{
                  ...S.btn,
                  background: on ? "#0f766e" : (n === 0 ? "#fafafa" : "#f3f4f6"),
                  color: on ? "#fff" : (n === 0 ? "#9ca3af" : "#374151"),
                  borderColor: on ? "#0f766e" : "#d1d5db",
                  fontSize: 12,
                }}>
                {f.label} ({n})
              </button>
            );
          })}
        </div>

        {items.length === 0 ? (
          <div style={S.muted}>Nothing here.</div>
        ) : (
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Branch</th>
                <th style={S.th}>Item</th>
                <th style={S.th}>Status</th>
                <th style={S.th}>Where</th>
                <th style={S.th}>Raised</th>
                <th style={S.th}>Label</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.delivery_id}>
                  <td style={S.td}>{displayName(it.branch_name, masked)}</td>
                  <td style={S.td}>
                    {it.description || <span style={S.muted}>No description</span>}
                    <div style={S.muted}>
                      {it.barcode ? it.barcode : `#${it.delivery_id}`}
                      {it.order_type === "collection" && " · collection"}
                      {it.requires_pin && " · needs a code"}
                      {it.is_urgent && " · urgent"}
                    </div>
                  </td>
                  <td style={{ ...S.td, background: STATUS_BG[it.status] }}>
                    {statusLabel(it)}
                    {it.attempts > 0 && (
                      <div style={S.muted}>
                        {it.attempts} attempt{it.attempts === 1 ? "" : "s"}
                      </div>
                    )}
                    {it.failure_reason && (
                      <div style={S.muted}>{it.failure_reason}</div>
                    )}
                  </td>
                  {/* "Where" is where the ETA belongs. The bare "On a round"
                      that used to sit here answered the question badly: the
                      person reading this is on the phone to the customer, and
                      "out with a driver" is the one thing they already knew. */}
                  <td style={S.td}>
                    {/* For a collection the useful answer to "where" is where
                        the driver is going, not which shelf it is on - it is
                        not on a shelf, that is the whole point. */}
                    {/* Once the driver has it, "where" is the van; once he has
                        put it back, it is the shelf. Before that it is the
                        pickup point he is going to. */}
                    {it.order_type === "collection"
                      ? (it.status === "collected"
                          ? <span>In {it.assigned_driver_name ? `${displayName(it.assigned_driver_name, masked)}'s` : "the driver's"} van</span>
                          : it.status === "delivered"
                            ? <span>{it.location_name || "Collections"} at {displayName(it.branch_name, masked)}</span>
                            : (it.pickup_address || it.pickup_postcode
                                ? <span>{[it.pickup_address, it.pickup_postcode].filter(Boolean).join(", ")}</span>
                                : <span style={S.muted}>Pickup address not set</span>))
                      : (it.location_name
                          || (it.round_id ? <span style={S.muted}>On a round</span>
                                          : <span style={S.muted}>—</span>))}
                    {it.order_type === "collection"
                      && it.status !== "collected" && it.status !== "delivered" && (
                      <div style={S.muted}>
                        {it.collect_when === "scheduled" && it.collect_at
                          ? `Due ${fmtDateTime(it.collect_at)}`
                          : "As soon as possible"}
                        {it.assigned_driver_name && ` · ${displayName(it.assigned_driver_name, masked)}`}
                      </div>
                    )}
                    {it.drops_away !== null && it.drops_away !== undefined && (
                      <div style={S.muted}>
                        {it.drops_away === 0
                          ? <b>Next drop</b>
                          : `${it.drops_away} drop${it.drops_away === 1 ? "" : "s"} away`}
                      </div>
                    )}
                    {etaLine(it) && (
                      <div style={S.muted} title={etaBasisText(it.eta_basis)}>
                        {etaLine(it)}
                      </div>
                    )}
                  </td>
                  <td style={S.td}>
                    {fmtDate(it.created_at)}
                    {/* The events that have actually happened to it, each from
                        its own timestamp. This used to print "Delivered" against
                        handed_over_at on every row, and handed_over_at is the
                        moment the parcel went INTO the van. */}
                    {eventLines(it).map((line) => (
                      <div key={line} style={S.muted}>{line}</div>
                    ))}
                  </td>
                  <td style={S.td}>
                    <button style={{ ...S.btn, padding: "3px 8px", fontSize: 12 }}
                      onClick={() => printLabel(it, c).catch((e) => setError(e.message))}>
                      Print QR
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Forms
// ---------------------------------------------------------------------------

const FIELDS = [
  { key: "full_name",     label: "Name" },
  { key: "address",       label: "Address" },
  { key: "postcode",      label: "Postcode" },
  { key: "phone",         label: "Phone" },
  { key: "email",         label: "Email" },
  { key: "external_ref",  label: "Reference" },
  { key: "delivery_note", label: "Delivery note" },
];

function CustomerForm({ initial, busy, error, duplicate, onChange, onSubmit, onCancel, submitLabel }) {
  return (
    <div style={S.panel}>
      {error && <div style={{ ...S.error, marginBottom: 10 }}>{error}</div>}
      {duplicate && (
        <div style={{ background: "#fffbeb", border: "1px solid #fde68a",
                      color: "#92400e", padding: 10, borderRadius: 6,
                      fontSize: 13, marginBottom: 10 }}>
          Somebody with this name already exists at this postcode. That may be
          right — two people can share an address — so this has been saved
          anyway and flagged rather than blocked.
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
        {FIELDS.map((f) => (
          <div key={f.key} style={f.key === "address" ? { gridColumn: "1 / -1" } : {}}>
            <div style={S.label}>{f.label}</div>
            <input style={S.input} value={initial[f.key] || ""}
              onChange={(e) => onChange(f.key, e.target.value)} />
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <button style={S.btnGo} disabled={busy} onClick={onSubmit}>
          {busy ? "Saving…" : submitLabel}
        </button>
        <button style={S.btn} disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function NewCustomer({ token, onCancel, onCreated }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      const d = await api("", token, { method: "POST", body: JSON.stringify(form) });
      onCreated(d.customer.id);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  return <CustomerForm initial={form} busy={busy} error={error}
    onChange={(k, v) => setForm((f) => ({ ...f, [k]: v }))}
    onSubmit={submit} onCancel={onCancel} submitLabel="Add customer" />;
}

/**
 * The opt-in for "notifications and updates" by email, saved the moment it is
 * ticked. An email address alone is not consent; this is. Without an address
 * the tick is shown disabled with the reason, so the office edits the customer
 * first rather than wondering why nothing arrives.
 */
function NotifyPreference({ customerId, hasEmail }) {
  const [pref, setPref] = useState(null);
  const [err, setErr] = useState(null);
  const auth = () => localStorage.getItem("token") || localStorage.getItem("authToken") || "";
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/messaging/customers/${customerId}/preferences`, { headers: { Authorization: `Bearer ${auth()}` } });
        const d = await res.json().catch(() => ({}));
        if (!cancelled) setPref(res.ok ? d : { notify_updates: false });
      } catch { if (!cancelled) setPref({ notify_updates: false }); }
    })();
    return () => { cancelled = true; };
  }, [customerId]);
  const toggle = async (on) => {
    setPref((p) => ({ ...p, notify_updates: on })); setErr(null);
    try {
      const res = await fetch(`${API_BASE}/api/messaging/customers/${customerId}/preferences`, {
        method: "PUT", headers: { Authorization: `Bearer ${auth()}`, "Content-Type": "application/json" },
        body: JSON.stringify({ notify_updates: on }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d?.error?.message || `HTTP ${res.status}`);
      setPref(d);
    } catch (e) { setErr(e.message); setPref((p) => ({ ...p, notify_updates: !on })); }
  };
  if (!pref) return null;
  return (
    <div style={{ fontSize: 12.5, marginTop: 4 }}>
      <label style={{ cursor: hasEmail ? "pointer" : "not-allowed", color: hasEmail ? "#111827" : "#9ca3af" }}
             title={hasEmail ? "Email this customer when their parcel moves, with a live map when it is out for delivery" : "Add an email address (Edit) to switch this on"}>
        <input type="checkbox" checked={!!pref.notify_updates} disabled={!hasEmail} onChange={(e) => toggle(e.target.checked)} />
        {" "}Receive in-flight notifications and updates by email
      </label>
      {err && <span style={{ color: "#b91c1c", marginLeft: 8 }}>{err}</span>}
    </div>
  );
}

function EditCustomer({ customer, token, onCancel, onSaved }) {
  const [form, setForm] = useState({ ...customer });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      const body = {};
      for (const f of FIELDS) body[f.key] = form[f.key] || null;
      await api(`/${customer.id}`, token, { method: "PATCH", body: JSON.stringify(body) });
      onSaved();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  return <CustomerForm initial={form} busy={busy} error={error}
    onChange={(k, v) => setForm((f) => ({ ...f, [k]: v }))}
    onSubmit={submit} onCancel={onCancel} submitLabel="Save changes" />;
}

/**
 * The driver picker for a collection.
 *
 * A LIST OF ROWS, NOT A DROPDOWN. A select can only show a name, and the
 * question being asked is not "which name" but "who can get there soonest" -
 * which needs a distance beside each one. The server has already sorted them
 * nearest first.
 *
 * A DRIVER WITH NO POSITION SAYS SO. Distance is measured from where somebody
 * actually is, and a driver whose phone has not reported has no distance. They
 * sort last and are labelled, because "no recent position" is a fact worth
 * knowing before you hand him a job, and blank space reads as zero.
 */
function DriverPicker({ drivers, busy, branchChosen, showDistance, hasPickup, value, onChange }) {
  if (!branchChosen) {
    return <div style={S.muted}>Choose a branch first.</div>;
  }
  if (busy && !drivers.length) {
    return <div style={S.muted}>Finding drivers…</div>;
  }
  if (!drivers.length) {
    return (
      <div style={S.muted}>
        No drivers are set up at this branch. Add one under Organisation before
        raising a collection here.
      </div>
    );
  }

  return (
    <div>
      {showDistance && !hasPickup && (
        <div style={{ ...S.muted, marginBottom: 6 }}>
          Add a pickup postcode to see how far each driver is from it.
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {drivers.map((d) => {
          const on = value === d.driver_id;
          return (
            <label key={d.driver_id}
              style={{
                display: "flex", alignItems: "center", gap: 10, cursor: "pointer",
                border: on ? "2px solid #0f766e" : "1px solid #d1d5db",
                background: on ? "#f0fdfa" : "#fff",
                borderRadius: 6, padding: on ? "7px 9px" : "8px 10px",
              }}>
              <input type="radio" name="collection-driver" checked={on}
                onChange={() => onChange(d.driver_id)} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontWeight: 600, fontSize: 13 }}>{d.full_name}</span>
                {!d.reachable && (
                  <span style={{ ...S.muted, marginLeft: 8 }}>
                    no app on file
                  </span>
                )}
              </span>
              {showDistance && (
                <span style={{ fontSize: 12, textAlign: "right", whiteSpace: "nowrap" }}>
                  {d.minutes_away === null ? (
                    <span style={S.muted}>
                      {hasPickup ? "No recent position" : ""}
                    </span>
                  ) : (
                    <>
                      <span style={{ fontWeight: 700, color: "#0f766e" }}>
                        about {d.minutes_away} min
                      </span>
                      <span style={S.muted}>
                        {" "}· {d.miles_away} mi away
                      </span>
                    </>
                  )}
                </span>
              )}
            </label>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Tell the driver the job is there.
 *
 * REAL, not a placeholder. POST /api/comms/messages already exists and writes
 * to `messages`, which the app polls - so a message sent now genuinely
 * arrives, just on the poll rather than instantly. What is NOT wired is the
 * Firebase push that would wake the phone, and the response says how many
 * devices could have been pushed to rather than claiming any were. A fake
 * button here would have been worse than none: somebody would send a message,
 * see it succeed, and assume the driver had been told.
 */
function DriverMessage({ token, driverId, driverName, defaultText }) {
  const [text, setText] = useState(defaultText || "");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const [error, setError] = useState(null);

  const send = async () => {
    const content = text.trim();
    if (!content) return;
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/comms/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ content, driverIds: [driverId] }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error((data && data.error && data.error.message)
          || `Could not send (${res.status})`);
      }
      setSent(data || {});
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  if (sent) {
    // The same honesty as the Communications screen: pushed, failed and why,
    // or no phone registered. A message the driver was not told about must
    // not read as one that buzzed.
    const pushed = Number(sent.pushDelivered || 0);
    const failed = Number(sent.pushFailed || 0);
    const noApp = sent.pushable === 0;
    const bad = failed > 0 || (sent.pushConfigured === false);
    return (
      <div style={{ border: `1px solid ${bad ? "#fecaca" : "#99f6e4"}`,
                    background: bad ? "#fef2f2" : "#f0fdfa",
                    borderRadius: 6, padding: "8px 10px" }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>
          Message saved for {driverName || "the driver"}.
        </div>
        <div style={S.muted}>
          {sent.pushConfigured === false
            ? "Push is not configured on the server, so it will not buzz their phone; they see it when they next open their messages."
            : noApp
              ? "They have no app registered, so it will not buzz their phone; they see it when they next open their messages."
              : pushed > 0
                ? "Pushed to their phone."
                : `The push failed${failed ? "" : ""}, so it will not buzz their phone; they see it when they next open their messages.`}
          {Array.isArray(sent.pushErrors) && sent.pushErrors.length > 0 && (
            <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
              {sent.pushErrors.map((e) => <li key={e}>{e}</li>)}
            </ul>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "8px 10px" }}>
      <div style={S.label}>Message {driverName || "the driver"}</div>
      {error && <div style={{ ...S.error, margin: "6px 0" }}>{error}</div>}
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={2}
        style={{ ...S.input, resize: "vertical", fontFamily: "inherit", marginTop: 4 }} />
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
        <button style={S.btnGo} disabled={busy || !text.trim()} onClick={send}>
          {busy ? "Sending…" : "Send"}
        </button>
        <span style={S.muted}>
          Saved to their messages and pushed to their phone if the app is registered.
        </span>
      </div>
    </div>
  );
}

/**
 * A delivery needs a BRANCH — it is the only place a branch enters this
 * screen. The customer belongs to the group; the parcel belongs to whichever
 * branch is sending it.
 */
function NewDelivery({ customerId, customer, token, onCancel, onCreated }) {
  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState({
    order_type: "delivery",
    collect_when: "soonest",
    // Seeded from the customer, and editable. A collection is USUALLY from
    // their address - a hospital discharge, a workplace or a neighbour is why
    // it is a field rather than an assumption.
    pickup_address: customer ? (customer.address || "") : "",
    pickup_postcode: customer ? (customer.postcode || "") : "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(null);

  const isCollection = form.order_type === "collection";

  useEffect(() => {
    api("/branches", token).then(setBranches).catch((e) => setError(e.message));
  }, [token]);

  // One branch means no choice to make; preselect rather than ask a question
  // with a single answer.
  useEffect(() => {
    if (branches.length === 1 && !form.account_id) {
      setForm((f) => ({ ...f, account_id: branches[0].account_id }));
    }
  }, [branches, form.account_id]);

  // Who could take this collection, and how far each of them is from the
  // pickup. Reloaded when the branch changes (different roster) or the pickup
  // postcode changes (different distances), and debounced because the postcode
  // field is typed into a character at a time.
  const [drivers, setDrivers] = useState([]);
  const [driversBusy, setDriversBusy] = useState(false);
  const branchId = form.account_id;
  const pickupPostcode = form.pickup_postcode;

  useEffect(() => {
    if (!isCollection || !branchId) { setDrivers([]); return undefined; }
    let live = true;
    setDriversBusy(true);
    const t = setTimeout(() => {
      const qs = new URLSearchParams({ account_id: String(branchId) });
      if (pickupPostcode) qs.set("postcode", pickupPostcode);
      api(`/collection_drivers?${qs.toString()}`, token)
        .then((d) => { if (live) setDrivers(d.drivers || []); })
        // A failure here costs the distance column, not the ability to raise
        // the job, so it does not take over the form's error line.
        .catch(() => { if (live) setDrivers([]); })
        .finally(() => { if (live) setDriversBusy(false); });
    }, 400);
    return () => { live = false; clearTimeout(t); };
  }, [isCollection, branchId, pickupPostcode, token]);

  // A driver picked and then made invalid by a branch change must not be
  // submitted silently against the new branch - the server would reject it,
  // but the user would have been looking at a name that was no longer there.
  useEffect(() => {
    if (!form.assigned_driver_id) return;
    if (drivers.some((d) => d.driver_id === form.assigned_driver_id)) return;
    setForm((f) => ({ ...f, assigned_driver_id: "" }));
  }, [drivers, form.assigned_driver_id]);

  const chosenDriver = drivers.find((d) => d.driver_id === form.assigned_driver_id) || null;

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      // Only a collection carries the collection fields. Sending them on a
      // delivery would write pickup and schedule columns onto a row where they
      // mean nothing, and they would then show up on screens reading them back.
      const payload = isCollection ? {
        ...form,
        // Local wall time from the picker, turned into an instant here where
        // the reader's own timezone is known. Left alone if it is unparseable
        // so the server gives the real error rather than this silently
        // dropping it.
        collect_at: form.collect_when === "scheduled" && form.collect_at
          ? (Number.isNaN(new Date(form.collect_at).getTime())
              ? form.collect_at
              : new Date(form.collect_at).toISOString())
          : undefined,
      } : {
        ...form,
        pickup_address: undefined,
        pickup_postcode: undefined,
        collect_when: undefined,
        collect_at: undefined,
        assigned_driver_id: undefined,
      };
      const d = await api(`/${customerId}/deliveries`, token,
        { method: "POST", body: JSON.stringify(payload) });
      setCreated({ id: d.delivery_id, orderType: d.order_type || form.order_type,
                   driverId: isCollection ? form.assigned_driver_id : null,
                   driverName: chosenDriver ? chosenDriver.full_name : null });
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  if (created) {
    const isColl = created.orderType === "collection";
    return (
      <div style={S.panel}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>
          {isColl ? "Collection" : "Delivery"} #{created.id} created.
        </div>
        <div style={{ ...S.muted, marginBottom: 10 }}>
          {isColl
            ? "The driver sees this in their job list from the day it is due. "
              + "Nothing is scanned at this end - the parcel is at the far end - "
              + "so the allocation is the only thing that tells them it exists. "
              + "Once collected it shows here as in their van, and as at the "
              + "depot's Collections shelf once they mark it returned."
            : "The label carries the delivery id. Name and address are printed "
              + "for the driver to read, not matched on, so a label printed "
              + "before an address correction cannot undo it."}
        </div>

        {/* The message step lives HERE rather than on the form, because until
            the job exists there is nothing truthful to say about it. */}
        {isColl && created.driverId && (
          <DriverMessage token={token}
            driverId={created.driverId}
            driverName={created.driverName}
            defaultText={`Collection #${created.id} has been added to your list.`} />
        )}

        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          {/* Printing is not wired up yet — deliberately not a dead button
              that looks live. */}
          <button style={{ ...S.btn, opacity: 0.5, cursor: "not-allowed" }} disabled
            title="Label printing is not built yet">
            Print label
          </button>
          <button style={S.btnGo} onClick={onCreated}>Done</button>
        </div>
      </div>
    );
  }

  return (
    <div style={S.panel}>
      {error && <div style={{ ...S.error, marginBottom: 10 }}>{error}</div>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
        <div>
          <div style={S.label}>Branch</div>
          <select style={S.input} value={form.account_id || ""}
            onChange={(e) => setForm((f) => ({ ...f, account_id: e.target.value }))}>
            <option value="">Choose a branch…</option>
            {branches.map((b) => (
              <option key={b.account_id} value={b.account_id}>{b.name}</option>
            ))}
          </select>
        </div>
        <div>
          <div style={S.label}>Type</div>
          <select style={S.input} value={form.order_type}
            onChange={(e) => setForm((f) => ({ ...f, order_type: e.target.value }))}>
            <option value="delivery">Delivery</option>
            <option value="collection">Collection</option>
          </select>
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <div style={S.label}>Description</div>
          <input style={S.input} value={form.description || ""}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <div style={S.label}>Notes</div>
          <input style={S.input} value={form.notes || ""}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
        </div>

        {/* ---------------------------------------------------------------
            COLLECTION EXTRAS

            A delivery reaches a driver because he picks the parcel up at the
            depot - the scan IS the assignment. Nobody scans a collection: the
            parcel is at the far end, so unless the office allocates it and
            says where and when, the driver has no way of knowing the job
            exists. Hence three fields that a delivery does not need.
            --------------------------------------------------------------- */}
        {isCollection && (
          <>
            <div style={{ gridColumn: "1 / -1", borderTop: "1px solid #e5e7eb",
                          paddingTop: 10, marginTop: 2 }}>
              <div style={{ ...S.label, color: "#0f766e" }}>Collection details</div>
              <div style={{ ...S.muted, marginTop: 4 }}>
                The driver collects it from the address below and brings it back
                to the branch chosen above. It stays on their list, marked as in
                the van, until they are back at the depot and mark it returned,
                which puts it on that branch's <b>Collections</b> shelf.
              </div>
            </div>

            <div style={{ gridColumn: "1 / -1" }}>
              <div style={S.label}>Pick up from</div>
              <input style={S.input} value={form.pickup_address || ""}
                placeholder="Where the driver is going"
                onChange={(e) => setForm((f) => ({ ...f, pickup_address: e.target.value }))} />
              <div style={{ ...S.muted, marginTop: 3 }}>
                Starts as the customer's address. Change it if the parcel is
                somewhere else.
              </div>
            </div>

            <div>
              <div style={S.label}>Pickup postcode</div>
              <input style={S.input} value={form.pickup_postcode || ""}
                onChange={(e) => setForm((f) => ({ ...f, pickup_postcode: e.target.value }))} />
            </div>

            <div>
              <div style={S.label}>When</div>
              <select style={S.input} value={form.collect_when || "soonest"}
                onChange={(e) => setForm((f) => ({ ...f, collect_when: e.target.value }))}>
                <option value="soonest">As soon as possible</option>
                <option value="scheduled">At a set time</option>
              </select>
            </div>

            {form.collect_when === "scheduled" && (
              <div style={{ gridColumn: "1 / -1" }}>
                <div style={S.label}>Collection time</div>
                {/* datetime-local, so the browser owns the calendar and the
                    clock. It emits local wall time with NO ZONE, which the
                    server would read in its own zone - an hour out through
                    British summer. Converted to a real instant at submit
                    instead, so both ends agree on which moment "10am" is. */}
                <input type="datetime-local" style={{ ...S.input, maxWidth: 260 }}
                  value={form.collect_at || ""}
                  onChange={(e) => setForm((f) => ({ ...f, collect_at: e.target.value }))} />
              </div>
            )}

            <div style={{ gridColumn: "1 / -1" }}>
              <div style={S.label}>Driver</div>
              <DriverPicker
                drivers={drivers}
                busy={driversBusy}
                branchChosen={!!form.account_id}
                showDistance={form.collect_when === "soonest"}
                hasPickup={!!form.pickup_postcode}
                value={form.assigned_driver_id || ""}
                onChange={(v) => setForm((f) => ({ ...f, assigned_driver_id: v }))} />
            </div>
          </>
        )}
        <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
          <input type="checkbox" checked={!!form.requires_pin}
            onChange={(e) => setForm((f) => ({ ...f, requires_pin: e.target.checked }))} />
          Needs a code at the door
        </label>
        <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
          <input type="checkbox" checked={!!form.is_urgent}
            onChange={(e) => setForm((f) => ({ ...f, is_urgent: e.target.checked }))} />
          Urgent
        </label>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <button style={S.btnGo}
          disabled={busy || !form.account_id
                    || (isCollection && !form.assigned_driver_id)
                    || (isCollection && form.collect_when === "scheduled" && !form.collect_at)}
          onClick={submit}>
          {busy ? "Creating…" : isCollection ? "Create collection" : "Create delivery"}
        </button>
        {isCollection && !form.assigned_driver_id && (
          <span style={{ ...S.muted, alignSelf: "center" }}>
            Pick a driver — nobody is told about a collection otherwise.
          </span>
        )}
        <button style={S.btn} disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}